/**
 * Système d'éléments et de faiblesses/résistances.
 * Chaque élément a des forces et des faiblesses.
 * Le multiplicateur final influe sur les dégâts et les chances de statut.
 */

export const ELEMENTS = {
  physical: { id: 'physical', name: 'Physique', icon: '⚔️', color: '#A0A0A0' },
  fire:     { id: 'fire',     name: 'Feu',     icon: '🔥', color: '#EF4444' },
  ice:      { id: 'ice',      name: 'Glace',   icon: '❄️', color: '#3B82F6' },
  lightning:{ id: 'lightning',name: 'Foudre',  icon: '⚡', color: '#F59E0B' },
  earth:    { id: 'earth',    name: 'Terre',   icon: '🌍', color: '#8B4513' },
  wind:     { id: 'wind',     name: 'Vent',    icon: '💨', color: '#10B981' },
  holy:     { id: 'holy',     name: 'Sacré',   icon: '✨', color: '#F5C518' },
  dark:     { id: 'dark',     name: 'Ténèbres',icon: '🌑', color: '#7C3AED' },
  water:    { id: 'water',    name: 'Eau',     icon: '💧', color: '#06B6D4' },
  nature:   { id: 'nature',   name: 'Nature',  icon: '🌿', color: '#22C55E' }
};

// interactions[attacker][defender] = multiplier
export const ELEMENT_CHART = {
  physical: { weak: [], strong: [], resist: [] },
  fire:     { weak: ['water', 'earth'], strong: ['ice', 'nature'], resist: [] },
  ice:      { weak: ['fire'], strong: ['wind', 'earth', 'water'], resist: [] },
  lightning:{ weak: ['earth'], strong: ['water', 'wind'], resist: [] },
  earth:    { weak: ['ice', 'water', 'nature'], strong: ['fire', 'lightning'], resist: [] },
  wind:     { weak: ['ice', 'lightning'], strong: ['earth'], resist: [] },
  holy:     { weak: ['dark'], strong: ['dark'], resist: [] },
  dark:     { weak: ['holy'], strong: ['holy'], resist: [] },
  water:    { weak: ['lightning', 'nature'], strong: ['fire', 'earth'], resist: [] },
  nature:   { weak: ['fire'], strong: ['water', 'earth'], resist: [] }
};

let _elementsSource: any = null;
let _elementChartSource: any = null;

export function setElementsRegistry(registries: any) {
  _elementsSource = registries?.elements || null;
  _elementChartSource = registries?.elementChart || null;
}

function getElements() {
  return _elementsSource || ELEMENTS;
}

function getElementChart() {
  return _elementChartSource || ELEMENT_CHART;
}

const DEFAULT_MULT = 1.0;
const STRONG_MULT = 1.5;
const WEAK_MULT = 0.65;

export function getElementMultiplier(attackerElement: string, defenderElement: string) {
  if (!attackerElement || !defenderElement || attackerElement === defenderElement) return DEFAULT_MULT;
  const info = getElementChart()[attackerElement];
  if (!info) return DEFAULT_MULT;
  if (info.strong.includes(defenderElement)) return STRONG_MULT;
  if (info.weak.includes(defenderElement)) return WEAK_MULT;
  return DEFAULT_MULT;
}

export function getElementLabel(elementId: string) {
  return getElements()[elementId]?.name || 'Inconnu';
}

export function getElementIcon(elementId: string) {
  return getElements()[elementId]?.icon || '❓';
}

export function getElementColor(elementId: string) {
  return getElements()[elementId]?.color || '#A0A0A0';
}

// ── Identité élémentaire d'un MONSTRE (défensive + offensive) ───────────────
// Chaque monstre du bestiaire porte un `element` (son thème). On en dérive des
// entrées fusionnées dans `monster.stats`, aux MÊMES clés que l'investissement
// joueur (lues par combat.ts et la fiche) :
//   • resistance_<element> (+) : il RÉSISTE son propre élément ;
//   • resistance_<contre>  (−) : il est FAIBLE à l'élément qui le contre ;
//   • affinity_<element>   (+) : ses attaques de son élément tapent un peu plus fort.
// Sans ça, tous les monstres normaux affichaient affinités/résistances à 0.
export const MONSTER_RESIST_OWN = 6;
export const MONSTER_RESIST_WEAK = -4;
export const MONSTER_AFFINITY_OWN = 6;

// Contre CANONIQUE (aligné sur la table des world boss, worldBosses.ts) :
// l'élément qui est « fort contre » la clé selon ELEMENT_CHART. `physical` n'a
// pas de contre → un monstre physique résiste sans faiblesse élémentaire.
export const CANONICAL_COUNTER: Record<string, string> = {
  fire: 'water', ice: 'fire', lightning: 'earth', earth: 'wind',
  wind: 'ice', water: 'lightning', nature: 'fire', holy: 'dark', dark: 'holy',
};

/** Élément qui contre `element` (ou null si aucun, ex. physical/inconnu). */
export function elementCounter(element?: string): string | null {
  return (element && CANONICAL_COUNTER[element]) || null;
}

/**
 * Stats élémentaires d'un monstre à fusionner dans `stats` : résiste son
 * élément, faible à son contre, affine ses attaques de son élément. Renvoie {}
 * pour un élément absent/inconnu.
 */
export function monsterElementalStats(element?: string): Record<string, number> {
  if (!element || !getElementChart()[element]) return {};
  const out: Record<string, number> = {
    [`affinity_${element}`]: MONSTER_AFFINITY_OWN,
    [`resistance_${element}`]: MONSTER_RESIST_OWN,
  };
  const counter = elementCounter(element);
  if (counter) out[`resistance_${counter}`] = MONSTER_RESIST_WEAK;
  return out;
}
